import { cn } from '@/lib/utils'
import ResponsiveImage from '@/v2/components/helpers/responsive-image'
import SectionHead from '@/v2/components/sections/landing-pages/section-head'
import SectionWrapper from '@/v2/components/wrappers/section-wrapper'
import { staggerContainer } from '@/v2/constants/motion'
import { ImageType, LinkType } from '@/v2/types'
import { domAnimation, LazyMotion, m } from 'framer-motion'
import Link from 'next/link'
import React from 'react'

type Question = {
  id: number
  title: string
  image?: ImageType
  description?: string[]
  links?: LinkType[]
}

interface OurPartnersSectionProps extends React.HTMLAttributes<HTMLDivElement> {
  title?: string
  description?: string
  isBgDark?: boolean
  className?: string
  sectionTitleClassName?: string
  cards: Question[]
}

const defaultCardData = {
  fedex: {
    title: 'FedEx',
    image: {
      src: '/images/v2/pages/common/fedex.png',
      alt: 'FedEx logo'
    },
    links: [
      { href: 'https://www.fedex.com/en-us/home.html', text: 'Home Page' },
      { href: 'https://www.fedex.com/en-us/shipping.html', text: 'Services' },
      { href: 'https://www.fedex.com/en-us/terms-of-use.html', text: 'Terms Of Use' }
    ]
  },
  dhl: {
    title: 'DHL',
    image: {
      src: '/images/v2/pages/common/dhl.png',
      alt: 'DHL'
    },
    links: [
      { href: 'https://www.dhl.com/global-en/home.html', text: 'Home Page' },
      { href: 'https://www.dhl.com/global-en/home/express.html', text: 'Services' },
      { href: 'https://www.dhl.com/global-en/home/footer/terms-of-use.html', text: 'Terms Of Use' }
    ]
  },
  ups: {
    title: 'UPS',
    image: {
      src: '/images/v2/pages/common/ups.png',
      alt: 'UPS'
    },
    links: [
      { href: 'https://www.ups.com/', text: 'Home Page' },
      { href: 'https://www.ups.com/us/en/shipping.page', text: 'Services' },
      { href: 'https://www.ups.com/us/en/help-center/legal-terms-conditions.page', text: 'Terms Of Use' }
    ]
  }
}

const OurPartnersSection: React.FC<OurPartnersSectionProps> = props => {
  const { title, description, className, isBgDark, sectionTitleClassName, cards, ...rest } = props
  const cardBackground = isBgDark ? 'bg-[#fff]' : 'bg-[#F9F8F8]'

  return (
    <LazyMotion features={domAnimation}>
      <m.section
        variants={staggerContainer()}
        initial='hidden'
        whileInView='show'
        viewport={{ once: true, amount: 0.25 }}
        className={cn('landing-pages-wr section-spacing', isBgDark ? 'bg-[#F9F8F8]' : '', className)}
      >
        <SectionWrapper {...rest}>
          <SectionHead
            title={title || 'Our Shipping Partners'}
            description={description}
            className={sectionTitleClassName}
          />

          <div className='grid md-mn:grid-cols-3 grid-cols-1 xl-min:gap-12 gap-3'>
            {cards?.map(card => {
              const defaultData = defaultCardData[card.title.toLowerCase() as keyof typeof defaultCardData]
              const cardTitle = card.title || defaultData.title
              const cardImage = card.image || defaultData.image
              const cardLinks = card.links || defaultData.links

              return (
                <article
                  key={card.id}
                  className={cn(
                    'rounded-md p-6 [&_h3]:text-[#0A9CFF] [&_h3]:text-[20px] [&_h3]:font-bold [&_h3]:mb-4 hover:shadow-lg transition-all',
                    cardBackground
                  )}
                >
                  {cardImage && (
                    <div className='mb-3'>
                      <ResponsiveImage src={cardImage.src} alt={cardImage.alt} className='w-[270px] h-[146px]' />
                    </div>
                  )}
                  <h3>{cardTitle}</h3>
                  {/* descriptions */}
                  {card.description && (
                    <div className='space-y-3'>
                      {card.description.map((description, index) => (
                        <p
                          key={index}
                          dangerouslySetInnerHTML={{ __html: description }}
                          className='[&_a]:underline [&_a]:font-medium'
                        />
                      ))}
                    </div>
                  )}
                  {/* links */}
                  {cardLinks && (
                    <div className='flex items-center justify-between mt-3 gap-2 underline font-medium text-sm flex-wrap'>
                      {cardLinks.map((link, index) => (
                        <Link key={index} href={link.href} target='_blank'>
                          {link.text}
                        </Link>
                      ))}
                    </div>
                  )}
                </article>
              )
            })}
          </div>
        </SectionWrapper>
      </m.section>
    </LazyMotion>
  )
}

export default OurPartnersSection
